import React, { useEffect } from 'react'
import {
  Box,
  Button,
  Field,
  Flex,
  Heading,
  Input,
  Message,
  Modal,
  Text,
  toast,
} from '@workwhile/ui'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { format } from 'date-fns'
import { useCushionChargePoliciesWorkflowsContextValue } from '../../../context/CushionChargePoliciesWorkflowsProvider'
import { useCreateCushionChargePolicyFlowContextValue } from '../context/CreateCushionChargePolicyFlowProvider'
import {
  CreateCushionChargePolicySchema,
  createCushionChargePolicySchema,
} from '../../../schemas/cushionChargePolicy'

function defaultStartsAt() {
  return format(new Date(), "yyyy-MM-dd'T'HH:mm")
}

export const CreateCushionChargePolicyFlowMain = () => {
  const {
    createCushionChargePolicyFlow: { isOpen, close },
  } = useCushionChargePoliciesWorkflowsContextValue()

  const {
    state: {
      mutationState: { isPending, errorMessage },
    },
    actions: { submit, reset: resetMutation },
  } = useCreateCushionChargePolicyFlowContextValue()

  const methods = useForm<CreateCushionChargePolicySchema>({
    resolver: zodResolver(createCushionChargePolicySchema),
    defaultValues: {
      chargeAfterMinutes: 30,
      startsAt: defaultStartsAt(),
    },
  })

  const {
    handleSubmit,
    register,
    formState: { errors },
    reset,
  } = methods

  useEffect(() => {
    if (!isOpen) return
    reset({
      chargeAfterMinutes: 30,
      startsAt: defaultStartsAt(),
    })
  }, [isOpen, reset])

  const handleClose = () => {
    reset()
    resetMutation()
    close()
  }

  const onSubmit = handleSubmit((data) => {
    submit(
      {
        chargeAfterMinutes: data.chargeAfterMinutes,
        startsAt: new Date(data.startsAt).toISOString(),
      },
      {
        onSuccess: () => {
          toast.success('Cushion charge policy created', {
            position: 'top-center',
          })
          handleClose()
        },
      }
    )
  })

  return (
    <Modal
      open={isOpen}
      onClose={handleClose}
      position="top"
      loading={isPending}
      showCancel
      customCta={
        <Flex>
          <Button variant="text" onClick={handleClose}>
            Cancel
          </Button>
          <Button variant="primary" onClick={onSubmit}>
            Add
          </Button>
        </Flex>
      }
    >
      <Box
        as="form"
        onSubmit={(event) => {
          event.preventDefault()
          onSubmit()
        }}
      >
        <Heading level={3} fontWeight="400" style={{ margin: 0 }}>
          Add cushion charge policy
        </Heading>
        <Text mt={2} fontSize={1} color="lightText">
          Cushion workers who stay longer than this are billed for the time they
          worked. Workers dismissed sooner are not charged.
        </Text>
        <Box mt={3}>
          <Field
            label="Charge after (minutes)"
            error={errors.chargeAfterMinutes?.message?.toString()}
          >
            <Input
              {...register('chargeAfterMinutes')}
              type="number"
              min={0}
              step={1}
              aria-label="Charge after minutes"
            />
          </Field>
        </Box>
        <Box mt={3}>
          <Field label="Starts at" error={errors.startsAt?.message?.toString()}>
            <Input
              {...register('startsAt')}
              type="datetime-local"
              aria-label="Starts at"
            />
          </Field>
        </Box>
        {errorMessage ? (
          <Message variant="error" mt={3} borderRadius="small">
            {errorMessage}
          </Message>
        ) : null}
      </Box>
    </Modal>
  )
}
